import { useCallback, useContext } from 'react';

import { useSetAtom } from 'jotai';

import {
    changedDaysAtom,
    changedOpenTypeAtom,
    changedTooltipAtom,
    changedTravelDateAtom,
    changedTravellerOrRoomAtom,
} from 'data/Atom/IntegratedOvernight';

import { CommunicationContext } from 'utility/Provide/Context/PageContext';

import { VerticalType } from 'types/common';
import { SwitchData } from 'types/legacy/backendInformation';

import { getHighlightData } from '../__helper/getSwitchDataConfig';
import { dataMapping } from '../__helper/integratedOvernightRouteMapping';
import { ChangedHighlightType, HighlightData } from '../types';

export const useSwitchDataConfig = () => {
    const highlightData = useHighlightData();

    const setOpenType = useSetAtom(changedOpenTypeAtom);
    const setTravellerOrRoom = useSetAtom(changedTravellerOrRoomAtom);
    const setTravelDate = useSetAtom(changedTravelDateAtom);
    const setDays = useSetAtom(changedDaysAtom);
    const setShowTooltip = useSetAtom(changedTooltipAtom);

    const initSwitchData = useCallback(() => {
        if (!highlightData) {
            return;
        }

        const { changedInfo, openType, showTooltip } = highlightData;

        if (!openType) {
            setOpenType(ChangedHighlightType.None);

            return;
        }

        setOpenType(openType);
        setTravellerOrRoom(changedInfo.travellerOrRoom);
        setTravelDate(changedInfo.travelDate);
        setDays(changedInfo.days);
        setShowTooltip(showTooltip);
    }, [
        highlightData,
        setOpenType,
        setTravellerOrRoom,
        setTravelDate,
        setDays,
        setShowTooltip,
    ]);

    return { initSwitchData };
};

export const useSwitchData = (type: VerticalType): SwitchData | undefined => {
    const mappingType = dataMapping[type];
    const namespace = useContext(CommunicationContext);

    return (
        namespace?.components?.backendInformationFunc.getData<SwitchData>(
            mappingType
        ) ?? undefined
    );
};

const useHighlightData = (): HighlightData | null => {
    const hotelData: SwitchData | undefined = useSwitchData(VerticalType.Hotel);
    const holidayHomeData: SwitchData | undefined = useSwitchData(
        VerticalType.HolidayHome
    );
    const cmxData: SwitchData | undefined = useSwitchData(VerticalType.Cmx);

    switch (true) {
        case !!hotelData?.redirectUrl:
            return hotelData?.changedFilter
                ? getHighlightData(hotelData.changedFilter)
                : null;
        case !!holidayHomeData?.redirectUrl:
            return holidayHomeData?.changedFilter
                ? getHighlightData(holidayHomeData.changedFilter)
                : null;
        case !!cmxData?.redirectUrl:
            return cmxData?.changedFilter
                ? getHighlightData(cmxData.changedFilter)
                : null;
        default:
            return null;
    }
};
